import {
  MappedNode,
  NodeMap,
  VariableConsumptionMap,
} from '@app-velocity/common/types';

import { SimpleTextNode } from '../../types';
import { isNodeWithChildren } from '../../utils/isNodeWithChildren';
import { readReactions } from '../../utils/readReactions';
import { isNodeWithReactions } from '../../utils/isNodeWithReactions';
import { getAbsoluteCoordinates } from '../../utils/getAbsoluteCoordinates';
import { isNodeWithSize } from '../../utils/isNodeWithSize';
import { isNodeVisible } from '../../utils/isNodeVisible';
import { getVariantsPointsData } from './getPointsFromVariants';
// import { getNodeTextSize } from '../../utils/textNodeFontSize';

const getNodeVisibilityCondition = (node: BaseNode) => {
  const visibleWhen: { [variableId: string]: string | number | boolean }[] = [];

  if ('boundVariables' in node) {
    const visibilityConditions = node.boundVariables?.visible;

    if (
      visibilityConditions &&
      visibilityConditions.type === 'VARIABLE_ALIAS'
    ) {
      const visibilityCondition: { [variableId: string]: boolean } = {};

      visibilityCondition[visibilityConditions.id] = true;

      visibleWhen.push(visibilityCondition);
    }
  }

  return visibleWhen;
};

const getNodeCharacters = (node: BaseNode) => {
  if (node.type === 'TEXT') return node.characters;

  if (node.type === 'INSTANCE') {
    const allNodeNestesTextNodes: TextNode[] = node.findAll(
      (subNode) => subNode.type === 'TEXT'
    ) as TextNode[];
    const allCharacters = allNodeNestesTextNodes
      .map((textNode) => textNode.characters)
      .join(' ');

    return allCharacters;
  }

  return undefined;
};

const contains = (outer: MappedNode, inner: MappedNode) => {
  return (
    outer.x! <= inner.x! &&
    outer.y! <= inner.y! &&
    outer.x! + outer.width! >= inner.x! + inner.width! &&
    outer.y! + outer.height! >= inner.y! + inner.height!
  );
};

const isNodeBehindAndContaining = (node: MappedNode, nodesMap: NodeMap) => {
  const everyNodeDetails = Object.values(nodesMap);

  return everyNodeDetails.some(
    (other) =>
      other.order <= node.order && other.id !== node.id && contains(node, other)
  );
};

const traverseNode = async (
  node: BaseNode,
  pageNode: BaseNode,
  nodeMap: NodeMap,
  allTextNodes: SimpleTextNode[],
  tempLayer: FrameNode,
  warnings: { nodeName: string; nodeId: string }[],
  flowPageId: string,
  handledVariantIDs: string[],
  order: number,
  byId: Map<string, SceneNode>,
  variantsMap: Map<
    string,
    {
      destinationIds: Map<string, string[]>;
      componentsMap: Map<string, ComponentNode>;
    }
  >,
  variables: Variable[],
  dontSkipVariants: boolean
) => {
  if (
    isNodeVisible(node) ||
    ('variableConsumptionMap' in node &&
      'VISIBLE' in (node.variableConsumptionMap as VariableConsumptionMap))
  ) {
    // console.log('TRAVERSING NODE', node.id, node.name, node.type)
    const nodeId = node.id.replace(/[:;]/g, '-');

    nodeMap[nodeId] = {
      id: node.id,
      name: node.name,
      type: node.type,
      characters: getNodeCharacters(node),
      absolutePosition: isNodeWithSize(node)
        ? getAbsoluteCoordinates(node)
        : undefined,
      width: isNodeWithSize(node) ? node.width : undefined,
      height: isNodeWithSize(node) ? node.height : undefined,
      opacity: 'opacity' in node ? node.opacity : undefined,
      visible: 'visible' in node ? node.visible : undefined,
      visibleIn: flowPageId,
      order,
    };

    if (
      'variableConsumptionMap' in node &&
      'VISIBLE' in (node.variableConsumptionMap as VariableConsumptionMap)
    ) {
      const visibleCondition = getNodeVisibilityCondition(node);

      if (visibleCondition) {
        nodeMap[nodeId] = {
          ...nodeMap[nodeId],
          dynamic: true,
          visibleWhen: visibleCondition,
        };

        if ('opacity' in node) {
          // hiding node that can be conditionally displayed
          // node.visible = false;
          node.opacity = 0;
        }
      }
    }

    if (isNodeWithSize(node) && isNodeWithSize(pageNode)) {
      const parentPosition = getAbsoluteCoordinates(pageNode);
      const parentX = parentPosition.x;
      const parentY = parentPosition.y;

      const nodePosition = getAbsoluteCoordinates(node);

      nodeMap[nodeId] = {
        ...nodeMap[nodeId],
        x: nodePosition.x - parentX,
        y: nodePosition.y - parentY,
      };
    }

    // @TODO needs better way of handling
    if (node.type === 'INSTANCE') {
      // if node is in the background overlapping fully with nodes on the foreground the variant generation should be skipped (because it will be rendered on top)
      // @TODO find some solution
      if (!isNodeBehindAndContaining(nodeMap[nodeId], nodeMap)) {
        const variantsData = await getVariantsPointsData(
          node,
          pageNode,
          tempLayer,
          handledVariantIDs,
          warnings,
          order,
          byId,
          variantsMap,
          variables,
          dontSkipVariants
        );
        nodeMap[nodeId] = {
          ...nodeMap[nodeId],
          ...variantsData,
        };
      }
    }

    if ('overlayPositionType' in node)
      nodeMap[nodeId] = {
        ...nodeMap[nodeId],
        overlayPositionType: node.overlayPositionType,
      };

    if (isNodeWithReactions(node) && node.reactions.length > 0) {
      const actions = await readReactions(node, node, pageNode, allTextNodes);
      nodeMap[nodeId] = {
        ...nodeMap[nodeId],
        actions: actions.length > 0 ? actions : undefined,
      };
    }

    if (!isNodeWithChildren(node)) return null;
    if ((nodeMap[nodeId].variants || []).length > 0) return null;

    for (const childNode of node.children) {
      await traverseNode(
        childNode,
        pageNode,
        nodeMap,
        allTextNodes,
        tempLayer,
        warnings,
        flowPageId,
        handledVariantIDs,
        order + 1,
        byId,
        variantsMap,
        variables,
        dontSkipVariants
      );
    }
  }
};

// node can be a flow page, partial, variant or dynamic node
export const getNodesMap = async (
  node: BaseNode,
  type: 'page' | 'partial' | 'dynamic' | 'variant',
  flowPageId: string,
  handledVariantIDs: string[],
  byId: Map<string, SceneNode>,
  variantsMap: Map<
    string,
    {
      destinationIds: Map<string, string[]>;
      componentsMap: Map<string, ComponentNode>;
    }
  >,
  variables: Variable[],
  dontSkipVariants: boolean
) => {
  const nodeMap: NodeMap = {};

  const tempLayer: FrameNode | null = byId.get(
    'velocity-temp-data'
  ) as FrameNode;

  if (!tempLayer) return { nodeMap: {}, mappedTextNodes: [], warnings: [] };

  // const flowPageNode: BaseNode | null =
  //   shallowFindInContainer(flowPageId, tempLayer) || node;
  const flowPageNode: SceneNode | null =
    byId.get(flowPageId) || (node as SceneNode);
  // console.log('GOT FLOW PAGE NODE', flowPageNode);
  if (!flowPageNode) return { nodeMap: {}, mappedTextNodes: [], warnings: [] };

  // console.log('STARTED GETTING TEXT NODES');
  // const allTextNodes = isNodeWithChildren(node)
  //   ? (node.findAll(
  //       (n) => n.type === 'TEXT' && !!n.visible && n.opacity > 0
  //     ) as TextNode[])
  //   : [];
  // console.log('STOPPED GETTING TEXT NODES', allTextNodes);

  // const mappedTextNodes: SimpleTextNode[] = allTextNodes.map((textNode) => ({
  //   id: textNode.id,
  //   characters: textNode.characters,
  //   absolutePosition: getAbsoluteCoordinates(textNode),
  //   width: textNode.width,
  //   height: textNode.height,
  //   size: getNodeTextSize(textNode),
  // }));
  const mappedTextNodes: SimpleTextNode[] = [];

  const warnings: {
    nodeName: string;
    nodeId: string;
  }[] = [];

  await traverseNode(
    node,
    flowPageNode,
    nodeMap,
    mappedTextNodes,
    tempLayer,
    warnings,
    flowPageId,
    handledVariantIDs,
    1,
    byId,
    variantsMap,
    variables,
    dontSkipVariants
  );

  return { nodeMap, mappedTextNodes, warnings };
};
